<template>
  <div class="modern-services page-theme page-theme--sky">
    <page-stripes side="right" :colors="stripeColors" />
    <section class="hero container">
      <div>
        <p class="eyebrow">NEXT SERVICES</p>
        <h1>kokono プロダクトスイート</h1>
        <p class="lead">
          プロフェッショナル向けの kokono pro、ビジュアルコラボの kokono
          lite、キャリアドキュメントの kokono cv。
          機能ではなく「体験」を軸にした最新のサービスページです。
        </p>
        <div class="actions">
          <a
            class="btn primary"
            href="https://www.kokono.cloud/"
            target="_blank"
            rel="noopener"
            >資料請求</a
          >
        </div>
      </div>
    </section>

    <section class="section container product-cards">
      <article
        v-for="product in products"
        :key="product.id"
        :id="product.id"
        class="product-card"
        :class="{ 'is-coming-soon': product.comingSoon }"
      >
        <div class="product-card__text">
          <div class="product-card__status" v-if="product.comingSoon">
            Coming Soon!
          </div>
          <p class="eyebrow">{{ product.tag }}</p>
          <h2>{{ product.title }}</h2>
          <p>{{ product.desc }}</p>
          <ul>
            <li v-for="feature in product.features" :key="feature">
              {{ feature }}
            </li>
          </ul>
          <div class="chips">
            <span v-for="chip in product.chips" :key="chip">{{ chip }}</span>
          </div>
          <div class="links" v-if="!product.comingSoon">
            <a :href="product.href" target="_blank" rel="noopener"
              >公式サイト</a
            >
            <a :href="product.demo" target="_blank" rel="noopener"
              >動画を見る</a
            >
          </div>
          <div class="links links--disabled" v-else>
            <span>Coming Soon!</span>
          </div>
        </div>
        <div class="product-card__media">
          <video
            v-if="product.mediaType === 'video'"
            :src="product.media"
            :poster="product.poster"
            autoplay
            muted
            playsinline
            loop
          ></video>
          <div v-else class="product-card__media-wrapper">
            <img :src="product.media" :alt="product.title" />
            <div v-if="product.comingSoon" class="coming-soon-overlay">
              <span>Coming Soon!</span>
            </div>
          </div>
        </div>
      </article>
    </section>

    <section class="section container comparison">
      <div class="section__header">
        <p class="eyebrow">CHOICE GUIDE</p>
        <h2>どのプロダクトを選ぶべきか？</h2>
      </div>
      <div class="comparison__grid">
        <article v-for="row in comparison" :key="row.label">
          <p class="label">{{ row.label }}</p>
          <div class="cells">
            <span>{{ row.pro }}</span>
            <span>{{ row.lite }}</span>
            <span>{{ row.cv }}</span>
          </div>
        </article>
      </div>
    </section>

    <section class="section container workflow">
      <div class="section__header">
        <p class="eyebrow">WORKFLOW</p>
        <h2>kokono スイート導入ステップ</h2>
      </div>
      <ol>
        <li v-for="step in workflow" :key="step.title">
          <p class="step-title">{{ step.title }}</p>
          <p class="step-desc">{{ step.desc }}</p>
        </li>
      </ol>
    </section>

    <section class="section container faq">
      <div class="section__header">
        <p class="eyebrow">FAQ</p>
        <h2>よくある質問</h2>
      </div>
      <div class="faq__grid">
        <article v-for="item in faq" :key="item.q">
          <p class="question">{{ item.q }}</p>
          <p class="answer">{{ item.a }}</p>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { ref } from "vue";
import PageStripes from "../components/PageStripes.vue";

export default {
  name: "ModernServices",
  components: {
    PageStripes,
  },
  setup() {
    const stripeColors = ["#dbeafe", "#c5e8ff", "#aeddff", "#7ec8f7"];
    const products = ref([
      {
        id: "pro",
        tag: "UI仕様書エディター",
        title: "kokono pro",
        desc: "仕様書は開発フローの要。多言語・差分対応で行き違いなく迅速スムーズな開発をサポートします。MCP対応でAIコラボソリューションとして威力を発揮します。",
        features: [
          "ノーコード仕様書テンプレート",
          "ロールベース権限とレビュー",
          "生成AIによる仕様補完",
        ],
        chips: ["プロダクトマネージャー", "UI/UXデザイナー", "SIer"],
        href: "https://www.kokono.cloud/",
        demo: "https://youtu.be/WBppSF03yS8?si=FkOaC8u9-LPUQiDj",
        mediaType: "image",
        media: "/image/pro512.png",
      },
      {
        id: "lite",
        tag: "画像コメントサービス",
        title: "kokono lite",
        desc: "画像やスクリーンショットに直接コメントを付け、共有 URL だけで関係者がレビュー可能。BtoBでもBtoCでも使いやすい軽量版。",
        features: [
          "ドラッグ&ドロップで即アップロード",
          "ライブカーソルとコメントログ",
          "ゲストロールにPIN共有",
        ],
        chips: ["マーケ担当", "代理店", "カスタマーサクセス"],
        href: "https://app.kokono.cloud/",
        demo: "https://app.kokono.cloud/assets/kokono_tutorial-CobmF_er.webm",
        mediaType: "video",
        media: "https://app.kokono.cloud/assets/kokono_tutorial-CobmF_er.webm",
      },
      {
        id: "cv",
        tag: "履歴書ビルダー",
        title: "kokono cv",
        desc: "AI支援でらくらく作成できます。",
        features: ["ライブプレビュー", "アートアルバム", "職務経歴アシストAI"],
        chips: ["求職者", "リクルーター", "人材紹介"],
        href: "https://cv.kokono.cloud/",
        demo: "https://www.kokono.cloud/",
        mediaType: "image",
        media: "/image/cv512x512.png",
      },
      {
        id: "king",
        tag: "不動産管理",
        title: "賃貸王 (Chintai King)",
        desc: "大切な資産を育む物件管理アシスタントです。賃貸オーナー、物件管理業務、シェアハウス経営をサポートします。複数物件の収支・契約から修繕履歴までワンストップで管理できます。",
        features: [
          "書類の長期保管と一元管理",
          "世代交代・引き継ぎの共同運用",
          "修繕のビフォーアフター画像管理",
          "収支ダッシュボードと勘定科目会計",
          "LINE通知で契約更新リマインダー",
        ],
        chips: ["個人賃貸オーナー", "管理業務", "シェアハウス運営"],
        href: "https://king.kokono.cloud/",
        demo: "https://king.kokono.cloud/",
        mediaType: "image",
        media: "/image/king2.png",
      },
      {
        id: "cv-enterprise",
        tag: "エンタープライズHR",
        title: "CV エンタープライズ",
        desc: "現在鋭意開発中で、専門職のスキルを見える化し社内の人的資産をサポート。AIによる分析やスキルアップ支援を備えたAI時代のHRツールです。",
        features: [
          "専門職のスキル可視化",
          "社内リソースマッピング",
          "AIスキルアップ提案",
        ],
        chips: ["人事", "HRBP", "経営企画"],
        href: "#",
        demo: "#",
        mediaType: "image",
        media: "/image/cv512x512.png",
        comingSoon: true,
      },
    ]);

    const comparison = ref([
      {
        label: "対象部門",
        pro: "PdM/UX/開発",
        lite: "マーケ/カスタマー",
        cv: "HR/求職者",
      },
      {
        label: "主な成果",
        pro: "仕様の可視化",
        lite: "フィードバック高速化",
        cv: "書類品質向上",
      },
      { label: "導入時間", pro: "1-2週間", lite: "即日", cv: "即日" },
      {
        label: "コラボレーション",
        pro: "ドキュメント同時編集",
        lite: "リアルタイムコメント",
        cv: "共有リンク配布",
      },
      {
        label: "エクスポート",
        pro: "PDF / JSON",
        lite: "PNG / 注釈履歴",
        cv: "PDF / Web",
      },
    ]);

    const workflow = ref([
      {
        title: "1. オンラインデモ",
        desc: "ニーズをヒアリングし、最適な構成を提案します。",
      },
      {
        title: "2. テナント作成",
        desc: "サンドボックス環境でワークフローを検証します。",
      },
      {
        title: "3. スタッフトレーニング",
        desc: "役割ごとに短時間のオンボーディングで定着化。",
      },
      {
        title: "4. データ連携",
        desc: "API / SSO で既存の業務基盤と接続します。",
      },
    ]);

    const faq = ref([
      {
        q: "既存ページと併存できますか？",
        a: "はい。`/next-*` ルートに公開し、十分な検証後に `/` を置き換えられます。",
      },
      {
        q: "言語ローカライズは？",
        a: "Vue i18n で簡単に多言語化可能。日本語/英語トグルもページヘッダーで準備できます。",
      },
      {
        q: "デザイン変更の影響範囲は？",
        a: "既存 Home/Services コンポーネントを触らないため、リグレッションを最小化できます。",
      },
    ]);

    return {
      stripeColors,
      products,
      comparison,
      workflow,
      faq,
    };
  },
};
</script>

<style lang="scss" scoped>
.modern-services {
  background: #ffffff;
  color: #1e293b;
  min-height: 100vh;
  padding-bottom: 5rem;
  position: relative;
  z-index: 1;
}

.container {
  width: min(1180px, 92vw);
  margin: 0 auto;
}

.hero {
  padding: 5rem 0 3rem;
  text-align: center;
}

.eyebrow {
  font-size: 0.8rem;
  letter-spacing: 0.4em;
  color: var(--theme-accent);
}

h1 {
  font-size: clamp(2.8rem, 4vw, 3.9rem);
  margin: 1rem 0;
  color: #1e293b;
}

.lead {
  color: #64748b;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 auto;
  text-align: center;
}

.actions {
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
}

.btn {
  border-radius: 999px;
  padding: 0.85rem 1.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease;
}

.btn.primary {
  background: linear-gradient(
    120deg,
    var(--theme-accent-light),
    var(--theme-accent)
  );
  color: var(--theme-btn-text);
}

.btn.ghost {
  border: 1px solid var(--theme-accent-pale);
  color: #475569;
  background: #ffffff;
}

.section {
  padding: 3rem 0;
}

.section__header h2 {
  color: #1e293b;
}

.product-cards {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

$product-card-grad-top: #ffffff;
$product-card-grad-flat: #e0ebf2;
$product-card-line: #e4eaebd6;

.product-card {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  background: linear-gradient(
    to bottom,
    $product-card-grad-top 0,
    $product-card-grad-flat calc(100% - 28px),
    $product-card-line calc(100% - 28px),
    $product-card-line calc(100% - 22px),
    $product-card-grad-flat calc(100% - 22px),
    $product-card-grad-flat 100%
  );
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--theme-accent-light);
  border-radius: 30px 30px 0 0;
  padding: 2rem;
  max-width: 920px;
  margin: 0 auto;
  transition: transform 0.35s ease, box-shadow 0.35s ease,
    border-color 0.35s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.06);
}

.product-card:hover {
  transform: translateY(-4px);
  border-color: var(--theme-accent-mid);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.product-card.is-coming-soon {
  border-style: dashed;
  border-color: var(--theme-accent-light);
}

.product-card__status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--theme-accent);
  border: 1px solid var(--theme-accent-light);
  border-radius: 999px;
  padding: 0.25rem 0.9rem;
  margin-bottom: 0.75rem;
}

.product-card__text p {
  color: #64748b;
}

.links--disabled {
  justify-content: center;
  color: #94a3b8;
}

.links--disabled span {
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 20px;
  padding: 0.4rem 1.2rem;
}

.product-card__text ul {
  margin: 1rem 0;
  padding-left: 0;
  list-style: none;
  color: #64748b;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.chips span {
  background: var(--theme-bg-alt);
  border: 1px solid var(--theme-accent-light);
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  font-size: 0.85rem;
  color: var(--theme-btn-text);
}

.links {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.links a {
  color: var(--theme-accent);
  font-weight: 600;
}

.product-card__media {
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-card__media video,
.product-card__media img,
.product-card__media-wrapper {
  width: 100%;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06);
  background: #ffffff;
}

.product-card__media-wrapper {
  position: relative;
  overflow: hidden;
}

.product-card__media-wrapper img {
  width: 100%;
  display: block;
}

.coming-soon-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-6deg);
  background: linear-gradient(120deg, #f97316, #fcd34d);
  color: #1e293b;
  padding: 0.6rem 1.5rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 10px 25px rgba(249, 115, 22, 0.4);
}

#pro .product-card__media img {
  padding: 1rem;
}

.comparison__grid {
  display: grid;
  gap: 1rem;
}

.comparison__grid article {
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 1rem;
}

.comparison__grid .label {
  color: #1e293b;
  font-weight: 600;
}

.comparison__grid .cells {
  display: grid;
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
  color: #64748b;
}

.workflow ol {
  list-style: none;
  counter-reset: steps;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.2rem;
  padding-left: 0;
}

.workflow li {
  position: relative;
  background: #ffffff;
  border-radius: 18px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-top: 3px solid var(--theme-accent-pale);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.workflow li::before {
  counter-increment: steps;
  content: counter(steps);
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-weight: 700;
  color: #e2e8f0;
  font-size: 2rem;
}

.faq__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
}

.faq__grid article {
  background: #ffffff;
  padding: 1.4rem;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--theme-accent-light);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.question {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1e293b;
}

.faq__grid article p:last-child {
  color: #64748b;
}

@media (max-width: 768px) {
  .product-card {
    grid-template-columns: 1fr;
  }

  .links {
    flex-wrap: wrap;
  }
}
</style>
